import React, { useState, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import MaskedInput from 'react-text-mask'

import cn from 'classnames'
import styles from './EmailReceiptModal.module.scss'
import useGetIsMobileScreen from '../../hooks/useGetIsMobileScreen'
import { submitEmail, submitText } from './ReceiptUtils'
import { ToastManager } from '../../utils/toastManager'
import { VALID_EMAIL, VALID_SMS_NUMBER } from '../../VNUtil/Validator'

export const RECEIPT_TYPES = {
  EMAIL: 'email',
  TEXT: 'phone number',
}

const ReceiptModal = ({ onClose, receiptType, allowMultiple, order, paymentId, className }) => {
  const classNames = cn(styles.emailReceiptModal, className)
  const [recipientInfo, setRecipientInfo] = useState({ isValid: false, recipients: '' })
  const isMobileViewPort = useGetIsMobileScreen()

  const isSendDisabled = useMemo(() => {
    return !recipientInfo.isValid || !recipientInfo?.recipients
  }, [recipientInfo])

  const subTextMessage = useMemo(() => {
    if (receiptType === RECEIPT_TYPES.TEXT) {
      return null
    }

    if (allowMultiple) {
      return isMobileViewPort
        ? 'Separate multiple ' + receiptType + 's with\n commas'
        : 'Separate multiple ' + receiptType + 's with commas'
    }

    return 'Enter ' + receiptType
  }, [allowMultiple, isMobileViewPort, receiptType])

  const dispatch = useDispatch()

  const onRecipientSubmit = () => {
    if (!recipientInfo.recipients.length) {
      ToastManager.error(`The ${receiptType} cannot be empty.`)
    }

    if (isSendDisabled) return

    if (receiptType === RECEIPT_TYPES.EMAIL) {
      dispatch(submitEmail(recipientInfo, order, paymentId, setRecipientInfo))
    } else if (receiptType === RECEIPT_TYPES.TEXT) {
      dispatch(submitText(recipientInfo, order, setRecipientInfo))
    }
    onClose()
  }

  const inputKeyDownHandler = (e) => {
    if (e.key !== 'Enter') return
    onRecipientSubmit()
  }

  const inputPhoneKeyDownHandler = (e) => {
    if (e.key === 'Enter') {
      onRecipientSubmit()
      return
    }

    if (e.key === 'Backspace' || e.keyCode === 229) {
      const recipients = recipientInfo.recipients

      // remove last digit on (###) and (###)###-
      if (recipients.length === 3 || recipients.length === 6) {
        e.preventDefault()
        setRecipientInfo({ ...recipientInfo, recipients: recipients.slice(0, -1) })
      }
    }
  }

  const onEmailChangeHandler = (e) => {
    const val = e.target.value
    setRecipientInfo({ ...recipientInfo, isValid: VALID_EMAIL.test(val), recipients: val })
  }

  const onPhoneChangeHandler = (e) => {
    const recipients = e.target.value.replace(/\D/g, '')

    setRecipientInfo({ ...recipientInfo, isValid: VALID_SMS_NUMBER.test(e.target.value), recipients })
  }

  /**
   * Dynamically select the input field based on the receipt type
   *
   * @param {HTMLInputElement.PropTypes} props
   * @returns
   */
  const InputComponent = (props) => {
    if (receiptType === RECEIPT_TYPES.EMAIL) {
      return <input type="email" onKeyDown={inputKeyDownHandler} onChange={onEmailChangeHandler} {...props} />
    }

    return (
      <MaskedInput
        mask={['(', /[1-9]/, /\d/, /\d/, ')', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
        type="tel"
        onKeyDown={inputPhoneKeyDownHandler}
        onChange={onPhoneChangeHandler}
        {...props}
      />
    )
  }

  return (
    <div className={classNames}>
      <div className={styles.overlay}></div>
      <div className={styles.content}>
        <div className={styles.emailText}>Enter {receiptType}</div>
        <div className={styles.emailSubtext}>{subTextMessage}</div>
        <div className={styles.inputContainer}>
          <InputComponent autoFocus value={recipientInfo.recipients} className={styles.inputField} />
        </div>
        <div className={styles.buttonContainer}>
          <div onClick={onClose} className={styles.cancelButton}>
            Cancel
          </div>
          <div className={styles.partition} />
          <div onClick={onRecipientSubmit} className={styles.sendButton} disabled={isSendDisabled}>
            Send
          </div>
        </div>
      </div>
    </div>
  )
}

ReceiptModal.defaultProps = {
  onClose: () => {},
}

ReceiptModal.propTypes = {
  onClose: PropTypes.func,
}

export default ReceiptModal
